/**
 * Cliente/Persona 360 — panel lateral desde la agenda (Persona 360, Fase 5).
 *
 * Clic en una persona -> drawer con su resumen (recurrencia de Fase 1), sus
 * citas, acceso al CRM y acciones rápidas, SIN salir de la agenda. Consume
 * endpoints existentes; no duplica datos. Vocabulario tropicalizado por vertical.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  X, Star, Sparkles, CalendarPlus, MessageCircle, PhoneCall, User, ExternalLink, Clock, FileText,
} from 'lucide-react';

const ESTADO_ES = {
  scheduled: 'Agendada', confirmed: 'Confirmada', completed: 'Realizada',
  cancelled: 'Cancelada', no_show: 'No asistió', pending_deposit: 'Pendiente depósito',
};

export default function ClientePanel360({ leadId, open, onClose, onNuevaCita }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [dict, setDict] = useState({});
  const [tab, setTab] = useState('resumen');
  const [cargando, setCargando] = useState(false);

  const L = (k, fb) => (dict?.dictionary?.[k]) || fb;

  useEffect(() => {
    if (!open || !leadId) return;
    setCargando(true); setTab('resumen');
    api.get('/tenant/business/dictionary').then(r => setDict(r.data || {})).catch(() => {});
    api.get(`/tenant/crm/personas/${leadId}/panel`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setCargando(false));
  }, [open, leadId]);

  if (!open) return null;
  const p = data?.persona || {};
  const rec = data?.recurrencia || {};
  const citas = data?.citas || [];
  const telDigits = (p.phone || '').replace(/\D/g, '');

  const fecha = (iso, conHora) => iso
    ? new Date(iso).toLocaleString('es-MX', conHora ? { dateStyle: 'medium', timeStyle: 'short' } : { dateStyle: 'medium' })
    : '—';

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose} data-testid="cliente-panel-360">
      <div className="w-full max-w-md h-full bg-white shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* header */}
        <div className="px-4 py-3 border-b border-[#E2E8F0]">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-[#0F172A] truncate">{p.display_name || (cargando ? 'Cargando…' : '—')}</h2>
                {rec.es_recurrente
                  ? <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full"><Star className="w-3 h-3" /> Recurrente</span>
                  : (rec.num_visitas > 0
                    ? <span className="text-[10px] text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded-full">{L('client', 'Cliente')}</span>
                    : <span className="inline-flex items-center gap-1 text-[10px] text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded-full"><Sparkles className="w-3 h-3" /> Nuevo</span>)}
              </div>
              <div className="text-xs text-[#64748B]">{p.phone || p.email || ''}</div>
            </div>
            <button onClick={onClose} aria-label="Cerrar" className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-4 h-4" /></button>
          </div>
          {/* acciones rápidas */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            <Button size="sm" onClick={() => onNuevaCita?.(p)} data-testid="panel-nueva-cita">
              <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Nueva {L('appointment', 'cita').toLowerCase()}
            </Button>
            {telDigits && (
              <Button size="sm" variant="outline" onClick={() => window.open(`https://wa.me/${telDigits}`, '_blank')}>
                <MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp
              </Button>
            )}
            {telDigits && (
              <Button size="sm" variant="outline" onClick={() => { window.location.href = `tel:+${telDigits}`; }}>
                <PhoneCall className="w-3.5 h-3.5 mr-1" /> Llamar
              </Button>
            )}
          </div>
        </div>

        {/* tabs */}
        <div className="flex border-b border-[#E2E8F0] text-xs">
          {['resumen', 'citas', 'crm'].map(t => (
            <button key={t} onClick={() => setTab(t)}
                    className={`px-3 py-2 font-medium ${tab === t ? 'text-[#0F172A] border-b-2 border-[#0F172A]' : 'text-[#94A3B8]'}`}
                    data-testid={`panel-tab-${t}`}>
              {t === 'resumen' ? 'Resumen' : t === 'citas' ? `${L('appointment', 'Citas')}` : 'CRM'}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {cargando && <p className="text-xs text-[#94A3B8]">Cargando…</p>}

          {tab === 'resumen' && !cargando && (
            <div className="grid grid-cols-2 gap-3 text-xs">
              <Dato t="Última visita" v={rec.ultima_visita ? `${fecha(rec.ultima_visita.fecha)}${rec.ultima_visita.servicio ? ' · ' + rec.ultima_visita.servicio : ''}` : '—'} />
              <Dato t="Próxima cita" v={rec.proxima_visita ? `${fecha(rec.proxima_visita.fecha, true)}${rec.proxima_visita.profesional ? ' · ' + rec.proxima_visita.profesional : ''}` : '—'} />
              <Dato t="Visitas" v={rec.num_visitas ?? 0} />
              <Dato t="Valor histórico" v={`$${Number(rec.valor_historico || 0).toLocaleString('es-MX')} ${rec.moneda || 'MXN'}`} />
              {rec.profesional_habitual && <Dato t={`${L('provider', 'Profesional')} habitual`} v={rec.profesional_habitual} />}
              {rec.frecuencia_media_dias != null && <Dato t="Frecuencia" v={`cada ~${rec.frecuencia_media_dias} días`} />}
              {(rec.cancelaciones > 0 || rec.no_shows > 0) && <Dato t="Cancel. / No-show" v={`${rec.cancelaciones || 0} / ${rec.no_shows || 0}`} />}
              <Dato t="Origen" v={p.source_type || '—'} />
            </div>
          )}

          {tab === 'citas' && !cargando && (
            <div className="space-y-2">
              {citas.length === 0 && <p className="text-sm text-[#64748B]">Sin citas registradas.</p>}
              {citas.map(c => (
                <div key={c.id} className="flex items-center justify-between border-b border-[#F1F5F9] py-2">
                  <div className="min-w-0">
                    <div className="text-sm text-[#0F172A] flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-[#94A3B8]" /> {fecha(c.starts_at, true)}
                    </div>
                    <div className="text-xs text-[#64748B]">{c.servicio || c.title}</div>
                  </div>
                  <span className="text-[10px] text-[#64748B] bg-[#F1F5F9] px-1.5 py-0.5 rounded-full">{ESTADO_ES[c.status] || c.status}</span>
                </div>
              ))}
            </div>
          )}

          {tab === 'crm' && !cargando && (
            <div className="space-y-2">
              {['clinic', 'health'].includes((dict?.vertical || '').toLowerCase()) && (
                <Button size="sm" variant="outline" className="w-full justify-start"
                        onClick={() => navigate(p.expediente_id
                          ? `/tenant/clinica?expediente=${p.expediente_id}`
                          : `/tenant/clinica?lead=${p.lead_id}&nombre=${encodeURIComponent(p.display_name || '')}`)}>
                  <FileText className="w-3.5 h-3.5 mr-1" /> {p.expediente_id ? 'Ver expediente' : 'Abrir/crear expediente'}
                </Button>
              )}
              <Button size="sm" variant="outline" className="w-full justify-start"
                      disabled={!p.contact_id}
                      onClick={() => p.contact_id && navigate(`/tenant/crm/contacts/${p.contact_id}`)}>
                <User className="w-3.5 h-3.5 mr-1" /> Ver historial completo del contacto
              </Button>
              <Button size="sm" variant="outline" className="w-full justify-start"
                      onClick={() => navigate(`/tenant/inbox?c=${p.contact_id || ''}`)}>
                <MessageCircle className="w-3.5 h-3.5 mr-1" /> Conversaciones
              </Button>
              <Button size="sm" variant="outline" className="w-full justify-start"
                      onClick={() => navigate(`/tenant/crm/leads?lead=${p.lead_id}`)}>
                <ExternalLink className="w-3.5 h-3.5 mr-1" /> Abrir en el CRM
              </Button>
              <div className="text-xs text-[#94A3B8] pt-1">
                {p.attribution?.medio ? `Medio: ${p.attribution.medio}` : ''}
                {p.attribution?.campana ? ` · Campaña: ${p.attribution.campana}` : ''}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Dato({ t, v }) {
  return (
    <div>
      <div className="text-[#94A3B8]">{t}</div>
      <div className="text-[#0F172A] font-medium">{v}</div>
    </div>
  );
}
